import { Box, deepMerge, Group, Text } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { MRT_ColumnDef, MRT_TableOptions } from 'mantine-react-table';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useSamplingQueries } from '../../data-access/queries/sampling';
import { PratiqueItem } from '../../data-access/types/pratiques';

import { useFiltersStore, useFiltersTableOptions } from '@data-access';
import { PfTable } from '@ui';

function formatPratiqueIds(
  pk: number,
  oleozeId: number | null | undefined,
): string {
  return `${pk} / ${oleozeId ?? '—'}`;
}

const PRACTICE_STATUS_COLORS: Record<string, string> = {
  annule: 'black',
  'a contractualiser': 'blue',
  'a lotir': 'orange',
  clos: 'green',
};

function practiceStatusColor(status: string | null | undefined): string | undefined {
  if (!status) return undefined;
  const key = status
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return PRACTICE_STATUS_COLORS[key];
}

function BooleanIcon({ value }: { value: boolean }) {
  return value ? (
    <IconCheck color="green" size={16} />
  ) : (
    <IconX color="red" size={16} />
  );
}

export const PratiquesTable: FC = () => {
  const { t } = useTranslation();
  const filtersTableOptions = useFiltersTableOptions<PratiqueItem>('pratiques');
  const samplingQueries = useSamplingQueries();
  const page = useFiltersStore.use['pratiques']().pagination.pageIndex + 1;
  const sorting = useFiltersStore.use['pratiques']().sorting;
  const filters = useFiltersStore.use['pratiques']().filters;

  const pratiques = useQuery({
    ...samplingQueries.pratiques.list({ page, sorting, filters }),
    placeholderData: keepPreviousData,
  });

  const columns = useMemo<MRT_ColumnDef<PratiqueItem>[]>(
    () => [
      {
        id: 'pratique_pk',
        accessorFn: (row) =>
          formatPratiqueIds(row.pratique_pk, row.oleoze_id),
        header: t('pratiques.columns.precifieldOleozeId'),
        Cell: ({ row, cell }) => {
          const status = row.original.practice_status;
          const statusColor = practiceStatusColor(status);

          return (
            <Group gap={6} wrap="nowrap">
              {statusColor ? (
                <Box
                  bg={statusColor}
                  h={8}
                  style={{ borderRadius: 999, flexShrink: 0 }}
                  w={8}
                />
              ) : null}
              <Text size="sm" title={status || undefined}>
                {cell.getValue<string>()}
              </Text>
            </Group>
          );
        },
      },
      {
        accessorKey: 'practice_status',
        header: t('pratiques.columns.practiceStatus'),
        Cell: ({ cell }) => cell.getValue<string | null>() || '-',
        filterVariant: 'select',
        mantineFilterSelectProps: {
          data: [
            { value: 'Annulé', label: 'Annulé' },
            { value: 'À contractualiser', label: 'À contractualiser' },
            { value: 'À lotir', label: 'À lotir' },
            { value: 'Clos', label: 'Clos' },
          ],
        },
      },
      {
        accessorKey: 'contract_id',
        header: t('pratiques.columns.contractId'),
      },
      {
        accessorKey: 'id_lot',
        header: t('pratiques.columns.lotId'),
        Cell: ({ cell }) => cell.getValue<string | null>() || '-',
      },
      {
        accessorKey: 'linked_contract_id',
        header: t('pratiques.columns.linkedContractId'),
        Cell: ({ cell }) => cell.getValue<number | null>() ?? '-',
      },
      {
        accessorKey: 'farm_name',
        header: t('pratiques.columns.farm'),
        Cell: ({ cell }) => cell.getValue<string | null>() || '-',
      },
      {
        accessorKey: 'organization_name',
        header: t('pratiques.columns.organization'),
      },
      {
        accessorKey: 'product_campaign',
        header: t('pratiques.columns.productCampaign'),
      },
      {
        accessorKey: 'campaign_year',
        header: t('pratiques.columns.campaignYear'),
      },
      {
        accessorKey: 'grandfather_clause',
        header: t('pratiques.columns.grandfatherClause'),
        Cell: ({ cell }) =>
          t(cell.getValue<boolean>() ? 'clauses.gp' : 'clauses.new'),
        filterVariant: 'checkbox',
      },
      {
        accessorKey: 'owner_name',
        header: t('pratiques.columns.owner'),
      },
      {
        accessorKey: 'disabled',
        header: t('pratiques.columns.disabled'),
        Cell: ({ cell }) => <BooleanIcon value={cell.getValue<boolean>()} />,
        filterVariant: 'checkbox',
      },
    ],
    [t],
  );

  const tableOptions: MRT_TableOptions<PratiqueItem> = deepMerge(
    {
      data: pratiques.data?.results ?? [],
      rowCount: pratiques.data?.count ?? 0,
      columns,
      state: {
        showLoadingOverlay: pratiques.isFetching && pratiques.isPlaceholderData,
      },
      initialState: {
        showColumnFilters: true,
      },
      mantinePaperProps: {
        h: '100%',
      },
    },
    filtersTableOptions,
  );

  return <PfTable<PratiqueItem> options={tableOptions} />;
};

export default PratiquesTable;
